/**
 * fiko-makro.insaat.ts — Mimarlık + Çelik makroları (saf üreteçler).
 *
 * Her makro, mevcut motorun ANLADIĞI IOzellik[] dizisi üretir (primitif kutu/silindir
 * + pad/pocket). Yeni motor YOK; FikoModellemeYonetici.rebuild() bunları CSG ile kurar.
 * Düzlem (ISketchDuzlem) origin + eksenler ile konumlanır → tüm feature dünya-uzayına oturur.
 *
 * Konvansiyon: makro lokal koordinat sistemi = XY taban düzlemi, +Z yukarı (kat yüksekliği).
 * Primitif kutu toolGeo'da BoxGeometry(pEn=U, pBoy=V, pYuk=normal) + translate(0,0,yuk/2)
 * → tabandan yukarı büyür. Bu yüzden makro origin'i hep parçanın TABAN orta-noktasıdır.
 */

import * as THREE from 'three';
import { ISketchDuzlem, FikoSketch } from './FikoSketch';
import { IOzellik } from './FikoModellemeYonetici';

/** XY tabanına paralel, verilen dünya origin'inde düzlem. */
export function xyDuzlem(ox = 0, oy = 0, oz = 0): ISketchDuzlem {
  return FikoSketch.duzlemHazir('xy', new THREE.Vector3(ox, oy, oz));
}

/** Bir kutu primitif feature'ı (taban orta-noktası origin'de, +Z'ye yükselir). */
export function kutuFeat(en: number, boy: number, yuk: number, duzlem: ISketchDuzlem, ekleme = true): IOzellik {
  return { tip: 'primitif', ekleme, primitifTip: 'kutu', pEn: en, pBoy: boy, pYuk: Math.max(0.2, yuk), duzlem };
}

/** Bir silindir primitif feature'ı (taban dairesi origin'de, +Z'ye yükselir). */
export function silindirFeat(cap: number, yuk: number, duzlem: ISketchDuzlem, ekleme = true): IOzellik {
  return { tip: 'primitif', ekleme, primitifTip: 'silindir', pCap: cap, pYuk: Math.max(0.2, yuk), duzlem };
}

export interface IMakroParam { [k: string]: number | string | boolean; }

/**
 * DUVAR — parametrik duvar (uzunluk × kalınlık × yükseklik), opsiyonel kapı + pencere boşluğu.
 * Lokal: uzunluk X (eksenU), kalınlık Y (eksenV), yükseklik Z.
 * Boşluklar çıkarma kutusu olarak eklenir (kalınlıktan biraz taşar → temiz boolean).
 */
export function duvarMakro(p: IMakroParam): IOzellik[] {
  const uz = +p['uzunluk'] || 300;
  const kal = +p['kalinlik'] || 20;
  const yuk = +p['yukseklik'] || 280;
  const kat = +p['kat'] || 0;            // kat yükseklik ofseti (Z)
  const taban = xyDuzlem(0, 0, kat);
  const out: IOzellik[] = [kutuFeat(uz, kal, yuk, taban, true)];

  // Kapı boşluğu (sol-merkez bölge): genişlik/yükseklik > 0 ise çıkar
  const kapiG = +p['kapiGenislik'] || 0;
  const kapiY = +p['kapiYukseklik'] || 0;
  if (kapiG > 0 && kapiY > 0) {
    const kapiX = (typeof p['kapiKonum'] === 'number') ? +p['kapiKonum'] : -uz * 0.25;  // duvar boyunca konum (merkez ref)
    const d = xyDuzlem(kapiX, 0, kat + kapiY / 2);                                       // boşluk merkezi yüksekliğe ortalı
    out.push(kutuFeat(kapiG, kal + 2, kapiY, xyDuzlem(kapiX, 0, kat - 1), false));       // tabandan kapiY yukarı, +1 alt taşma
  }

  // Pencere boşluğu (sağ bölge): denizlik yüksekliğinden başlar
  const pencG = +p['pencereGenislik'] || 0;
  const pencY = +p['pencereYukseklik'] || 0;
  if (pencG > 0 && pencY > 0) {
    const pencX = (typeof p['pencereKonum'] === 'number') ? +p['pencereKonum'] : uz * 0.25;
    const denizlik = (typeof p['pencereDenizlik'] === 'number') ? +p['pencereDenizlik'] : 90;
    out.push(kutuFeat(pencG, kal + 2, pencY, xyDuzlem(pencX, 0, kat + denizlik), false));
  }
  return out;
}

/**
 * DÖŞEME / PLAK — yatay döşeme plağı (en × boy × kalınlık), istenen kat kotunda.
 * Lokal: en X, boy Y, kalınlık Z. Origin = plak alt-yüzü merkezi.
 */
export function dosemeMakro(p: IMakroParam): IOzellik[] {
  const en = +p['en'] || 400;
  const boy = +p['boy'] || 400;
  const kal = +p['kalinlik'] || 20;
  const kot = +p['kot'] || 0;            // alt-yüz Z kotu
  return [kutuFeat(en, boy, kal, xyDuzlem(0, 0, kot), true)];
}

/**
 * KOLON — dikdörtgen veya yuvarlak kolon (yükseklik boyunca), istenen taban kotunda.
 * Lokal: en X, derinlik Y, yükseklik Z. Origin = kolon taban merkezi.
 */
export function kolonMakro(p: IMakroParam): IOzellik[] {
  const yuvarlak = p['yuvarlak'] === true || p['yuvarlak'] === 'true' || +p['yuvarlak'] === 1;
  const yuk = +p['yukseklik'] || 280;
  const kot = +p['kot'] || 0;
  const taban = xyDuzlem(0, 0, kot);
  if (yuvarlak) {
    const cap = +p['cap'] || 40;
    return [silindirFeat(cap, yuk, taban, true)];
  }
  const en = +p['en'] || 40;
  const der = +p['derinlik'] || 40;
  return [kutuFeat(en, der, yuk, taban, true)];
}

/**
 * KİRİŞ — yatay taşıyıcı kiriş (uzunluk × genişlik × yükseklik), istenen kotta.
 * Lokal: uzunluk X, genişlik Y, yükseklik Z. Origin = kiriş alt-yüzü merkezi.
 */
export function kirisMakro(p: IMakroParam): IOzellik[] {
  const uz = +p['uzunluk'] || 400;
  const gen = +p['genislik'] || 20;
  const yuk = +p['yukseklik'] || 40;
  const kot = +p['kot'] || 240;          // kiriş alt-yüz kotu (kat tavanı civarı)
  return [kutuFeat(uz, gen, yuk, xyDuzlem(0, 0, kot), true)];
}

/**
 * ÇELİK PROFİL — IPE/HEA/HEB/L (köşebent) kesiti, profil boyunca extrude (pad).
 * Kesit profili sketch elemanları (dikdortgen/cizgi) ile çizilir → pad ile uzatılır.
 * Tek bir pad feature döner (kesit kapalı konturu motor profilShape ile alır).
 *
 * Profil ekseni: kesit XY düzleminde, uzunluk +Z boyunca (pad normal yönü = düzlem normali).
 * Bu yüzden düzlem XY (normal +Z), pad param = uzunluk.
 */
export function celikProfilMakro(p: IMakroParam): IOzellik[] {
  const tip = String(p['profilTip'] || 'IPE');
  const h = +p['h'] || 200;              // yükseklik (kesit Y)
  const b = +p['b'] || 100;              // başlık (flanş) genişliği (kesit X)
  const tw = +p['tw'] || 6;              // gövde et kalınlığı
  const tf = +p['tf'] || 9;              // başlık et kalınlığı
  const uzunluk = +p['uzunluk'] || 1000; // profil boyu (pad)
  const kot = +p['kot'] || 0;

  const els = kesitElemanlari(tip, h, b, tw, tf);
  if (!els.length) return [];
  const duzlem = xyDuzlem(0, 0, kot);    // kesit XY, uzunluk +Z
  return [{ tip: 'pad', ekleme: true, param: Math.max(1, uzunluk), elemanlar: els, duzlem }];
}

/** Çelik kesiti → sketch eleman dizisi (kapalı kontur, motor profilShape ile alır).
 * Eleman formatı sketch.ekle ile uyumlu: { tip, noktalar:[{x,y}...], r? }.
 * I/H kesiti tek kapalı çizgi-zinciri olarak verilir; L köşebent de öyle. */
function kesitElemanlari(tip: string, h: number, b: number, tw: number, tf: number): any[] {
  const T = tip.toUpperCase();
  let pts: { x: number; y: number }[];
  if (T === 'L') {
    // Köşebent: dış köşe sol-altta, kalınlık tf (kanat), b×h dış ölçü
    const t = tf;
    pts = [
      { x: 0, y: 0 }, { x: b, y: 0 }, { x: b, y: t },
      { x: t, y: t }, { x: t, y: h }, { x: 0, y: h },
    ];
  } else {
    // I / H ailesi (IPE/HEA/HEB): merkez 0,0; simetrik
    const hx = b / 2, hy = h / 2, gw = tw / 2;
    pts = [
      { x: -hx, y: hy }, { x: hx, y: hy }, { x: hx, y: hy - tf },
      { x: gw, y: hy - tf }, { x: gw, y: -hy + tf }, { x: hx, y: -hy + tf },
      { x: hx, y: -hy }, { x: -hx, y: -hy }, { x: -hx, y: -hy + tf },
      { x: -gw, y: -hy + tf }, { x: -gw, y: hy - tf }, { x: -hx, y: hy - tf },
    ];
  }
  // kapalı çizgi-zinciri (motor zincirle() ile kapalı loop'a bağlar)
  const els: any[] = [];
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i], b2 = pts[(i + 1) % pts.length];
    els.push({ id: i + 1, tip: 'cizgi', noktalar: [{ x: a.x, y: a.y }, { x: b2.x, y: b2.y }] });
  }
  return els;
}

/**
 * BİRLEŞİM PLAKASI — dikdörtgen plaka + ızgara şablonlu bulon delikleri.
 * Plaka XY'de yatar (kalınlık +Z), delikler plakayı boydan boya deler (silindir çıkar).
 * Lokal: en X, boy Y. Origin = plaka alt-yüz merkezi.
 */
export function birlesimPlakasiMakro(p: IMakroParam): IOzellik[] {
  const en = +p['en'] || 200;
  const boy = +p['boy'] || 200;
  const kal = +p['kalinlik'] || 12;
  const kot = +p['kot'] || 0;
  const delikCap = +p['delikCap'] || 18;
  const sutun = Math.max(0, Math.round(+p['sutun'] || 2));
  const satir = Math.max(0, Math.round(+p['satir'] || 2));
  const adimX = +p['adimX'] || 80;
  const adimY = +p['adimY'] || 80;

  const out: IOzellik[] = [kutuFeat(en, boy, kal, xyDuzlem(0, 0, kot), true)];
  if (sutun > 0 && satir > 0 && delikCap > 0) {
    const x0 = -((sutun - 1) * adimX) / 2;
    const y0 = -((satir - 1) * adimY) / 2;
    for (let i = 0; i < sutun; i++) for (let j = 0; j < satir; j++) {
      const cx = x0 + i * adimX, cy = y0 + j * adimY;
      // deliği plaka alt-1 → üst+1 arası tam delecek şekilde uzat
      out.push(silindirFeat(delikCap, kal + 2, xyDuzlem(cx, cy, kot - 1), false));
    }
  }
  return out;
}

/** XZ düzlemi (normal +Y) — dikey kesit, +Y boyunca pad (çatı prizması için). */
export function xzDuzlem(ox = 0, oy = 0, oz = 0): ISketchDuzlem {
  return FikoSketch.duzlemHazir('xz', new THREE.Vector3(ox, oy, oz));
}

/**
 * ÇATI — beşik (gable) çatı prizması. Üçgen kesit XZ düzleminde, sırt +Y boyunca.
 * Lokal: taban eni X, derinlik Y (sırt yönü), yükseklik Z. Origin = taban orta-noktası.
 */
export function catiMakro(p: IMakroParam): IOzellik[] {
  const en = +p['en'] || 400;
  const boy = +p['boy'] || 400;
  const yuk = +p['yukseklik'] || 150;
  const sacak = +p['sacak'] || 0;
  const kot = +p['kot'] || 0;
  const en2 = en + 2 * sacak;
  const pts = [{ x: -en2 / 2, y: 0 }, { x: en2 / 2, y: 0 }, { x: 0, y: -yuk }];
  const els: any[] = [];
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i], b = pts[(i + 1) % pts.length];
    els.push({ id: i + 1, tip: 'cizgi', noktalar: [{ x: a.x, y: a.y }, { x: b.x, y: b.y }] });
  }
  return [{ tip: 'pad', ekleme: true, param: Math.max(1, boy + 2 * sacak), elemanlar: els, duzlem: xzDuzlem(0, -(boy / 2 + sacak), kot) }];
}

/**
 * MERDİVEN — düz kollu merdiven (basamak sayısı × genişlik × derinlik × rıht).
 * Her basamak tabandan o seviyeye dolu kutu (yan profil = merdiven).
 */
export function merdivenMakro(p: IMakroParam): IOzellik[] {
  const n = Math.max(1, Math.round(+p['basamak'] || 12));
  const gen = +p['genislik'] || 100;
  const der = +p['derinlik'] || 28;
  const rise = +p['basamakYuk'] || 18;
  const kot = +p['kot'] || 0;
  const out: IOzellik[] = [];
  for (let i = 0; i < n; i++) out.push(kutuFeat(gen, der, (i + 1) * rise, xyDuzlem(0, i * der - (n - 1) * der / 2, kot), true));
  return out;
}

/**
 * BİNA — komple parametrik bina: N kat (döşeme + 4 çevre duvarı) + çatı/teras.
 * Tek komutla tam bina montajı. Lokal: en X, boy Y, +Z kat yüksekliği.
 */
export function binaMakro(p: IMakroParam): IOzellik[] {
  const en = +p['en'] || 800;
  const boy = +p['boy'] || 1000;
  const katN = Math.max(1, Math.round(+p['katSayisi'] || 3));
  const katYuk = +p['katYuksekligi'] || 300;
  const dk = +p['duvarKalinlik'] || 20;
  const dosKal = +p['dosemeKalinlik'] || 20;
  const catiVar = p['cati'] === true || p['cati'] === 'true' || +p['cati'] === 1;
  const catiYuk = +p['catiYuksekligi'] || 200;
  const out: IOzellik[] = [];
  for (let k = 0; k < katN; k++) {
    const kot = k * katYuk;
    out.push(kutuFeat(en, boy, dosKal, xyDuzlem(0, 0, kot), true));
    const dz = kot + dosKal, dy = Math.max(10, katYuk - dosKal);
    out.push(kutuFeat(en, dk, dy, xyDuzlem(0, boy / 2 - dk / 2, dz), true));
    out.push(kutuFeat(en, dk, dy, xyDuzlem(0, -boy / 2 + dk / 2, dz), true));
    out.push(kutuFeat(dk, boy - 2 * dk, dy, xyDuzlem(-en / 2 + dk / 2, 0, dz), true));
    out.push(kutuFeat(dk, boy - 2 * dk, dy, xyDuzlem(en / 2 - dk / 2, 0, dz), true));
  }
  const ust = katN * katYuk;
  if (catiVar) out.push(...catiMakro({ en, boy, yukseklik: catiYuk, kot: ust }));
  else out.push(kutuFeat(en, boy, dosKal, xyDuzlem(0, 0, ust), true));
  return out;
}

/** Makro id → üreteç eşlemesi (FikoModellemeYonetici buradan çağırır). */
export const MAKRO_URETECLER: Record<string, (p: IMakroParam) => IOzellik[]> = {
  duvar: duvarMakro,
  doseme: dosemeMakro,
  kolon: kolonMakro,
  kiris: kirisMakro,
  cati: catiMakro,
  merdiven: merdivenMakro,
  bina: binaMakro,
  celikProfil: celikProfilMakro,
  birlesimPlakasi: birlesimPlakasiMakro,
};
